import ReceiptPrinterEncoder, {
  ThermalPrinterEncoderOption,
} from "@point-of-sale/receipt-printer-encoder"
import iconv from "iconv-lite"

export type Commands = Array<{
  commands: Array<{
    type: string
    value: any
    codepage?: string
    payload: number[]
    property?: string
  }>
  height: number
}>

type Lines = {
  type: string
  payload: number[]
  value?: any
  property?: string
}[][]

export default class ThermalPrinterEncoder extends ReceiptPrinterEncoder {
  constructor(options: ThermalPrinterEncoderOption) {
    super(options)
  }

  initializeWithMultiByte() {
    return this.initialize().raw([0x1c, 0x26])
  }

  /**
   * Apply raw ESC/POS `0x0a` command to force line break.
   * Required on some printer models to enforce line break if some custom formatting are enabled.
   *
   * Sample case:
   * Enabling align `center` and bold `true` at the same time may mess line breaks on some models.
   *
   * @returns this instance
   */
  lineFeed(): ThermalPrinterEncoder {
    return this.raw([0x0a])
  }

  encode(): Uint8Array {
    /* Get the commands */
    const commands = super.encode("commands") as any as Commands

    /* Build the lines */
    const lines: Lines = []

    for (const line of commands) {
      const buffer = []

      for (const item of line.commands) {
        if (item.type === "text") {
          buffer.push(...encodeText(item.value))
        } else if (item.type === "style") {
          buffer.push(
            Object.assign(item, {
              payload: encodeStyle(item.property, item.value),
            })
          )
        } else if (item.value || item.payload) {
          buffer.push(item)
        }
      }

      lines.push(buffer)
    }

    /* Build the array */

    let result = [0x1c, 0x26]
    let last = null

    for (const line of lines) {
      for (const item of line) {
        result.push(...item.payload)
        last = item
      }

      // Add newline character with some exceptions
      // This is to fix the excessive spacing at the receipt's bottom area
      // switch (last?.type) {
      //   case "newline":
      //   case "line":
      //   case "pulse":
      //   case "cut":
      //   case "align":
      //   case "style":
      //   // potentially using a lot of raw newline command
      //   case "raw":
      //     continue
      //   default:
      //     // console.log(`command with new line added: ${last?.type}`)
      //     result.push(0x0a)
      //     break
      // }
      result.push(0x0a)
    }

    /* If the last command is a pulse, do not feed */
    if (last && last.type === "pulse") {
      result = result.slice(0, 0 - 1)
    }

    return Uint8Array.from(result)
  }
}

function encodeText(value: string) {
  const fragment = iconv.encode(value, "GB18030")

  return [{ type: "text", payload: [...fragment] }]
}

function encodeStyle(property?: string, value?: any) {
  if (property === "bold") {
    return bold(value)
  }

  if (property === "underline") {
    return underline(value)
  }

  if (property === "italic") {
    return italic(value)
  }

  if (property === "invert") {
    return invert(value)
  }

  if (property === "size") {
    return size(value.width, value.height)
  }

  return []
}

/**
 * Enable or disable bold text
 * @param {boolean} value   Enable or disable bold text, optional, default toggles between states
 * @return {Array}         Array of bytes to send to the printer
 */
function bold(value: boolean) {
  let data = 0x00

  if (value) {
    data = 0x01
  }

  return [0x1b, 0x45, data]
}

/**
 * Enable or disable underline text
 * @param {boolean} value   Enable or disable underline text, optional, default toggles between states
 * @return {Array}         Array of bytes to send to the printer
 */
function underline(value: boolean) {
  let data = 0x00

  if (value) {
    data = 0x01
  }

  return [0x1b, 0x2d, data]
}

/**
 * Enable or disable italic text
 * @param {boolean} value   Enable or disable italic text, optional, default toggles between states
 * @return {Array}         Array of bytes to send to the printer
 */
function italic(value: boolean) {
  let data = 0x00

  if (value) {
    data = 0x01
  }

  return [0x1b, 0x34, data]
}

/**
 * Enable or disable inverted text
 * @param {boolean} value   Enable or disable inverted text, optional, default toggles between states
 * @return {Array}         Array of bytes to send to the printer
 */
function invert(value: boolean) {
  let data = 0x00

  if (value) {
    data = 0x01
  }

  return [0x1d, 0x42, data]
}

/**
 * Change text size
 * @param {number} width    Width of the text (1-8)
 * @param {number} height   Height of the text (1-8)
 * @return {Array}         Array of bytes to send to the printer
 */
function size(width: number, height: number) {
  return [0x1d, 0x21, (height - 1) | ((width - 1) << 4)]
}

export type { ThermalPrinterEncoderOption }
